import React, { useState, useEffect, useRef } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

// Simple Markdown Renderer Component
const MarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
  const colors = getThemeColors('dark');

  const parseMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactElement[] = [];
    let keyIndex = 0;

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        elements.push(<div key={`spacing-${index}`} style={{ height: spacing.sm }} />);
        return;
      }

      // Headers
      if (trimmedLine.startsWith('##')) {
        elements.push(
          <div
            key={`header2-${index}`}
            style={{
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.bold,
              color: colors.primary,
              marginTop: spacing.sm,
              marginBottom: spacing.sm,
              paddingBottom: spacing.xs,
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            {trimmedLine.replace(/^##\s*/, '')}
          </div>
        );
        return;
      }

      // Bold headers
      if (trimmedLine.match(/^\*\*.*\*\*$/)) {
        elements.push(
          <div
            key={`boldHeader-${index}`}
            style={{
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.bold,
              color: colors.text,
              marginTop: spacing.sm,
              marginBottom: spacing.sm,
            }}
          >
            {trimmedLine.replace(/^\*\*(.*)\*\*$/, '$1')}
          </div>
        );
        return;
      }

      // Bullet points
      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
        elements.push(
          <div
            key={`bullet-${index}`}
            style={{
              display: 'flex',
              marginTop: spacing.xs,
              marginBottom: spacing.xs,
              paddingLeft: spacing.sm,
            }}
          >
            <span style={{
              color: colors.primary,
              fontWeight: typography.fontWeight.bold,
              marginRight: spacing.sm,
              minWidth: 12,
            }}>
              •
            </span>
            <span style={{
              color: colors.text,
              fontSize: typography.fontSize.base,
              lineHeight: 1.4,
              flex: 1,
            }}>
              {renderInlineFormatting(trimmedLine.replace(/^[•-]\s*/, ''))}
            </span>
          </div>
        );
        return;
      }

      // Regular paragraph
      elements.push(
        <div
          key={`paragraph-${index}`}
          style={{
            color: colors.text,
            fontSize: typography.fontSize.base,
            lineHeight: 1.5,
            marginTop: spacing.xs,
            marginBottom: spacing.xs,
          }}
        >
          {renderInlineFormatting(trimmedLine)}
        </div>
      );
    });

    return elements;
  };

  const renderInlineFormatting = (text: string): React.ReactElement => {
    // Simple bold formatting
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return (
      <>
        {parts.map((part, index) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return (
              <strong
                key={index}
                style={{
                  fontWeight: typography.fontWeight.bold,
                  color: colors.text,
                }}
              >
                {part.slice(2, -2)}
              </strong>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  return <div>{parseMarkdown(content)}</div>;
};

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: string;
  questionId?: string;
}

interface Suggestion {
  id: string;
  text: string;
  icon: string;
}

const AIAssistant: React.FC = () => {
  const colors = getThemeColors('dark');

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const flatListRef = useRef<HTMLDivElement>(null);

  const quickSuggestions: Suggestion[] = [
    { id: '1', text: "What's my account balance?", icon: "wallet" },
    { id: '2', text: "Show my spending this month", icon: "analytics" },
    { id: '3', text: "How can I save more money?", icon: "trending-up" },
    { id: '4', text: "Show my recent transactions", icon: "list" },
    { id: '5', text: "Help me create a budget", icon: "calculator" },
    { id: '6', text: "Investment advice for me", icon: "bar-chart" },
  ];

  useEffect(() => {
    initializeChat();
  }, []);

  const initializeChat = () => {
    const welcomeMessage: Message = {
      id: Date.now().toString(),
      type: 'ai',
      content: `Hello there! 👋\n\nI'm your personal finance AI assistant. I can help you with:\n\n• Financial planning and budgeting\n• Investment advice\n• Savings strategies\n• Chama management tips\n• Spending analysis\n• Goal setting\n\nWhat would you like to know about your finances today?`,
      timestamp: new Date().toISOString(),
    };
    setMessages([welcomeMessage]);
    generateSuggestions();
  };

  const generateSuggestions = () => {
    // Generate personalized suggestions based on user data
    const personalizedSuggestions: Suggestion[] = [];

    // For now, just use the quick suggestions
    // In a real app, this would be based on user data like wallets, transactions, etc.
    setSuggestions([...personalizedSuggestions, ...quickSuggestions.slice(0, 4)]);
  };

  const sendMessage = async (messageText = inputText) => {
    if (!messageText.trim()) return;

    const questionId = `q_${Date.now()}`;
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString(),
      questionId,
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setLoading(true);

    try {
      // Generate AI response
      const aiResponse = await generateAIResponse(messageText, questionId);

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: aiResponse,
        timestamp: new Date().toISOString(),
        questionId,
      };

      setMessages(prev => [...prev, aiMessage]);
      scrollToBottom();
    } catch (error) {
      console.error('AI response failed:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: "I'm sorry, I'm having trouble processing your request right now. Please try again later.",
        timestamp: new Date().toISOString(),
        questionId,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const generateAIResponse = async (userInput: string, questionId: string): Promise<string> => {
    try {
      // Use context data directly for immediate responses
      const response = await generateContextualResponse(userInput, {
        // Mock user data - in real app this would come from context/API
        user: { first_name: 'User' },
        wallets: [],
        transactions: [],
        chamas: []
      });

      return response;
    } catch (error) {
      console.error('AI Response generation error:', error);
      return `I'm having trouble processing your request right now. Please try again in a moment.`;
    }
  };

  const generateContextualResponse = async (question: string, contextData: any): Promise<string> => {
    const { user, wallets, transactions, chamas } = contextData;
    const questionLower = question.toLowerCase();

    // Calculate real metrics from context data
    let totalBalance = 0;
    if (Array.isArray(wallets) && wallets.length > 0) {
      totalBalance = wallets.reduce((sum: number, wallet: any) => {
        const balance = wallet.balance || wallet.amount || 0;
        return sum + balance;
      }, 0);
    }

    const recentTransactions = Array.isArray(transactions) ? transactions.slice(0, 10) : [];
    const monthlyTransactions = Array.isArray(transactions) ? transactions.filter((t: any) => {
      const transactionDate = new Date(t.created_at || t.timestamp);
      const now = new Date();
      return transactionDate.getMonth() === now.getMonth() &&
             transactionDate.getFullYear() === now.getFullYear();
    }) : [];

    const monthlySpending = monthlyTransactions
      .filter((t: any) => t.type === 'withdrawal' || (t.amount && t.amount < 0))
      .reduce((sum: number, t: any) => sum + Math.abs(t.amount || 0), 0);

    const monthlyIncome = monthlyTransactions
      .filter((t: any) => t.type === 'deposit' || (t.amount && t.amount > 0))
      .reduce((sum: number, t: any) => sum + Math.abs(t.amount || 0), 0);

    // Balance inquiry
    if (questionLower.includes('balance') || questionLower.includes('money') || questionLower.includes('have')) {
      if (!wallets || (Array.isArray(wallets) && wallets.length === 0)) {
        return `💰 **Account Balance**\n\nI don't see any wallet data loaded yet. Please make sure you're logged in and your wallet is set up.\n\n**To get started:**\n• Check your internet connection\n• Make sure you're logged in\n• Try refreshing the app\n\nOnce your wallet is loaded, I'll be able to show you your exact balance!`;
      }

      let response = `💰 **Your Current Balance**\n\n`;
      response += `**Total Balance: ${formatCurrency(totalBalance)}**\n\n`;

      if (Array.isArray(wallets) && wallets.length > 0) {
        if (wallets.length > 1) {
          response += `**Wallet Breakdown:**\n`;
          wallets.forEach((wallet: any, index: number) => {
            const walletName = wallet.name || wallet.wallet_name || `Wallet ${index + 1}`;
            const walletBalance = wallet.balance || wallet.amount || 0;
            response += `• ${walletName}: ${formatCurrency(walletBalance)}\n`;
          });
          response += `\n`;
        }
      }

      if (recentTransactions.length > 0) {
        response += `**Recent Activity:**\n`;
        response += `• ${monthlyTransactions.length} transactions this month\n`;
        response += `• Monthly spending: ${formatCurrency(monthlySpending)}\n`;
        response += `• Monthly income: ${formatCurrency(monthlyIncome)}\n`;
      } else {
        response += `**No recent transactions found.**\n`;
        response += `Start making transactions to see your activity here.\n`;
      }

      return response;
    }

    // Spending analysis
    if (questionLower.includes('spend') || questionLower.includes('expense') || questionLower.includes('cost')) {
      let response = `📊 **Your Spending Analysis**\n\n`;
      response += `**This Month's Summary:**\n`;
      response += `• Total Spending: ${formatCurrency(monthlySpending)}\n`;
      response += `• Number of Transactions: ${monthlyTransactions.length}\n`;

      if (monthlyTransactions.length > 0) {
        response += `• Average per Transaction: ${formatCurrency(monthlySpending / monthlyTransactions.length)}\n`;
      }

      response += `\n**Recent Transactions:**\n`;
      recentTransactions.slice(0, 5).forEach((t: any) => {
        const date = new Date(t.created_at || t.timestamp).toLocaleDateString();
        const amount = formatCurrency(Math.abs(t.amount || 0));
        const type = t.type === 'withdrawal' ? '📉' : '📈';
        response += `${type} ${date}: ${amount}\n`;
      });

      return response;
    }

    // Transaction history
    if (questionLower.includes('transaction') || questionLower.includes('history') || questionLower.includes('recent')) {
      let response = `📋 **Your Transaction History**\n\n`;

      if (recentTransactions.length === 0) {
        response += `No transactions found. Start making transactions to see your history here.`;
        return response;
      }

      response += `**Recent Transactions:**\n`;
      recentTransactions.forEach((t: any) => {
        const date = new Date(t.created_at || t.timestamp).toLocaleDateString();
        const amount = formatCurrency(Math.abs(t.amount || 0));
        const type = t.type === 'withdrawal' ? '📉' : '📈';
        const description = t.description || 'Transaction';
        response += `${type} ${date} - ${description}: ${amount}\n`;
      });

      return response;
    }

    // Savings advice
    if (questionLower.includes('save') || questionLower.includes('saving')) {
      let response = `🎯 **Personalized Savings Strategy**\n\n`;
      response += `**Your Financial Snapshot:**\n`;
      response += `• Current Balance: ${formatCurrency(totalBalance)}\n`;
      response += `• Monthly Income: ${formatCurrency(monthlyIncome)}\n`;
      response += `• Monthly Expenses: ${formatCurrency(monthlySpending)}\n`;

      const savingsRate = monthlyIncome > 0 ? ((monthlyIncome - monthlySpending) / monthlyIncome) * 100 : 0;
      response += `• Current Savings Rate: ${savingsRate.toFixed(1)}%\n\n`;

      response += `**💡 Recommendations:**\n`;
      if (savingsRate < 10) {
        response += `• ⚠️ Your savings rate is low. Try to save at least 10% of your income\n`;
        response += `• Look for ways to reduce expenses\n`;
      } else if (savingsRate < 20) {
        response += `• 👍 Good start! Try to increase your savings rate to 20%\n`;
      } else {
        response += `• 🌟 Excellent savings rate! Keep it up!\n`;
      }

      response += `• Set up automatic savings transfers\n`;
      response += `• Build an emergency fund of 3-6 months expenses\n`;

      return response;
    }

    // Default response
    return `Hello ${user?.first_name || 'there'}! 👋\n\nI can help you with:\n\n• **Account balance** - "What's my balance?"\n• **Spending analysis** - "How much did I spend this month?"\n• **Transaction history** - "Show my recent transactions"\n• **Savings advice** - "How can I save more money?"\n\nWhat would you like to know about your finances?`;
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const scrollToBottom = () => {
    if (flatListRef.current) {
      flatListRef.current.scrollTop = flatListRef.current.scrollHeight;
    }
  };

  const renderMessage = (message: Message) => {
    const isUser = message.type === 'user';

    return (
      <div
        key={message.id}
        style={{
          display: 'flex',
          marginBottom: spacing.sm,
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          maxWidth: '100%',
        }}
      >
        {!isUser && (
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: spacing.sm,
              alignSelf: 'flex-start',
              marginTop: spacing.xs,
            }}
          >
            <span style={{ fontSize: 14, color: colors.white }}>✨</span>
          </div>
        )}

        <div
          style={{
            maxWidth: isUser ? '70%' : '80%',
            padding: spacing.md,
            borderRadius: borderRadius.lg,
            backgroundColor: isUser ? colors.primary : colors.surface,
            border: `1px solid ${colors.border}`,
            alignSelf: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          {isUser ? (
            <div
              style={{
                fontSize: typography.fontSize.base,
                color: colors.white,
                lineHeight: 1.4,
                marginBottom: spacing.xs,
              }}
            >
              {message.content}
            </div>
          ) : (
            <MarkdownRenderer content={message.content} />
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginTop: spacing.xs,
            }}
          >
            <span
              style={{
                fontSize: typography.fontSize.xs,
                color: isUser ? colors.white + '80' : colors.textSecondary,
                opacity: 0.7,
              }}
            >
              {new Date(message.timestamp).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderSuggestions = () => (
    <div style={{
      paddingLeft: spacing.md,
      paddingRight: spacing.md,
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
    }}>
      <div style={{
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.medium,
        marginBottom: spacing.sm,
        color: colors.textSecondary,
        textAlign: 'left',
        opacity: 0.7,
      }}>
        Try asking:
      </div>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
      }}>
        {suggestions.slice(0, 4).map((suggestion) => (
          <button
            key={suggestion.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: spacing.sm,
              paddingRight: spacing.sm,
              paddingTop: spacing.xs,
              paddingBottom: spacing.xs,
              borderRadius: borderRadius.full,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.backgroundSecondary,
              minHeight: 32,
              width: '48%',
              marginBottom: spacing.xs,
              cursor: 'pointer',
              fontSize: typography.fontSize.xs,
              color: colors.text,
              outline: 'none',
            }}
            onClick={() => sendMessage(suggestion.text)}
          >
            <span style={{ marginRight: spacing.xs, fontSize: 12 }}>
              {suggestion.icon === 'wallet' ? '💰' :
               suggestion.icon === 'analytics' ? '📊' :
               suggestion.icon === 'trending-up' ? '📈' :
               suggestion.icon === 'list' ? '📋' :
               suggestion.icon === 'calculator' ? '🧮' :
               suggestion.icon === 'bar-chart' ? '📊' : '💡'}
            </span>
            <span style={{ flex: 1, textAlign: 'left' }}>
              {suggestion.text}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    header: {
      textAlign: 'center',
      marginBottom: spacing.xl,
    },
    title: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    subtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    chatContainer: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      ...getShadowStyle('md'),
      marginBottom: spacing.lg,
      display: 'flex',
      flexDirection: 'column',
      height: '600px',
    },
    chatHistory: {
      flex: 1,
      overflowY: 'auto',
      marginBottom: spacing.lg,
      padding: spacing.sm,
    },
    inputContainer: {
      display: 'flex',
      gap: spacing.sm,
      borderTop: `1px solid ${colors.border}`,
      paddingTop: spacing.md,
    },
    input: {
      flex: 1,
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      fontSize: typography.fontSize.base,
      outline: 'none',
      resize: 'none',
    },
    button: {
      padding: `${spacing.md} ${spacing.lg}`,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: borderRadius.md,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    loadingIndicator: {
      display: 'flex',
      alignItems: 'center',
      padding: spacing.md,
      marginLeft: spacing.md,
      marginRight: spacing.md,
      marginBottom: spacing.sm,
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      border: `1px solid ${colors.border}`,
    },
    typingDots: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: spacing.sm,
    },
    dot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: colors.textSecondary,
      marginLeft: 2,
      marginRight: 2,
      animation: 'typing 1.4s infinite ease-in-out',
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="AI Financial Assistant"
        showBackButton={false}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={{ padding: spacing.md }}>
          <div style={styles.chatContainer}>
            <div style={styles.chatHistory} ref={flatListRef}>
              {messages.map(renderMessage)}

              {/* Loading indicator */}
              {loading && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: spacing.md,
                  marginLeft: spacing.md,
                  marginRight: spacing.md,
                  marginBottom: spacing.sm,
                  backgroundColor: colors.surface,
                  borderRadius: borderRadius.lg,
                  border: `1px solid ${colors.border}`,
                }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 14,
                      backgroundColor: colors.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ fontSize: 14, color: colors.white }}>✨</span>
                  </div>
                  <div style={styles.typingDots}>
                    <div style={styles.dot} />
                    <div style={{ ...styles.dot, animationDelay: '0.2s' }} />
                    <div style={{ ...styles.dot, animationDelay: '0.4s' }} />
                  </div>
                </div>
              )}

              {/* Suggestions */}
              {messages.length === 1 && renderSuggestions()}
            </div>

            <div style={styles.inputContainer}>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                placeholder="Ask me anything about your finances..."
                style={styles.input}
                rows={1}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!inputText.trim() || loading}
                style={{
                  ...styles.button,
                  opacity: inputText.trim() && !loading ? 1 : 0.6,
                  cursor: inputText.trim() && !loading ? 'pointer' : 'not-allowed',
                }}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;